<template>
  <div class="dataset-browser">
    <div class="db-heading">
      <h6><i class="fas fa-database"></i> Dataset</h6>
      <small class="text-muted">
        Pick a dataset to chart. Rasters are hidden — they can't be plotted.
      </small>
    </div>

    <!-- Breadcrumbs -->
    <div v-if="breadcrumbs && breadcrumbs.length" class="db-breadcrumbs">
      <template v-for="(crumb, i) in breadcrumbs">
        <button
          :key="'crumb-' + i"
          type="button"
          class="db-crumb"
          :class="{ 'is-current': i === breadcrumbs.length - 1 }"
          @click="launchFolder(crumb, true)"
        >
          <i v-if="i === 0" class="fas fa-home"></i>
          <span v-else>{{ crumb.name }}</span>
        </button>
        <span
          v-if="i + 1 !== breadcrumbs.length"
          :key="'sep-' + i"
          class="db-crumb-sep"
        >/</span>
      </template>
    </div>

    <!-- Selected dataset card (shown when one is active) -->
    <div v-if="selectedDataset" class="db-selected">
      <div class="db-selected-head">
        <i class="fas fa-check-circle db-selected-icon"></i>
        <div class="db-selected-body">
          <div class="db-selected-label">Active dataset</div>
          <strong>{{ selectedDataset.name }}</strong>
        </div>
        <button
          type="button"
          class="db-selected-clear"
          title="Clear selection"
          @click="$emit('selectDataset', null)"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Folders + datasets list -->
    <div class="db-list">
      <a
        v-for="folder in availableFolders"
        :key="'f-' + folder.id"
        class="db-row db-row-folder"
        @click="launchFolder(folder)"
      >
        <i class="fas fa-folder db-row-icon"></i>
        <div class="db-row-body">
          <div class="db-row-name">{{ folder.name }}</div>
          <div v-if="folder.description" class="db-row-desc">{{ folder.description }}</div>
        </div>
        <i class="fas fa-chevron-right db-row-chevron"></i>
      </a>

      <a
        v-for="dataset in plottableDatasets"
        :key="'d-' + dataset.id"
        class="db-row db-row-dataset"
        :class="{ 'is-active': selectedDataset && selectedDataset.id === dataset.id }"
        @click="selectDataset(dataset)"
      >
        <i :class="datasetIcon(dataset) + ' db-row-icon'"></i>
        <div class="db-row-body">
          <div class="db-row-name">
            {{ dataset.name }}
            <span v-if="dataset.dynamic" class="db-row-badge db-badge-dynamic" title="Live collector data">
              <i class="fas fa-satellite-dish"></i> Live
            </span>
            <span v-if="isAnalysisDataset(dataset)" class="db-row-badge db-badge-analysis" title="Recipe-generated analysis">
              <i class="fas fa-flask"></i> Analysis
            </span>
          </div>
          <div v-if="dataset.description" class="db-row-desc">{{ truncate(dataset.description) }}</div>
          <div class="db-row-meta">
            <span v-if="dataset.updatedOn">Updated {{ dataset.updatedOn | formatDate }}</span>
          </div>
        </div>
        <i v-if="selectedDataset && selectedDataset.id === dataset.id" class="fas fa-check db-row-chevron db-active-check"></i>
        <i v-else class="fas fa-chevron-right db-row-chevron"></i>
      </a>

      <div v-if="!availableFolders.length && !plottableDatasets.length" class="db-empty">
        <i class="far fa-folder-open"></i>
        <p class="mb-0">Nothing to chart here.</p>
        <small class="text-muted">
          Navigate back up or create a dataset first.
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'

export default {
  name: 'DatasetBrowser',
  props: {
    selectedDataset: {
      type: Object,
      default: null
    }
  },
  computed: {
    ...mapState(['datasets', 'folders', 'breadcrumbs', 'farm']),
    availableFolders () {
      return this.folders || []
    },
    plottableDatasets () {
      return (this.datasets || []).filter(d => this.isFiguresCompatible(d))
    }
  },
  methods: {
    selectDataset (dataset) {
      this.$emit('selectDataset', dataset)
    },

    launchFolder (folder, fromCrumb) {
      if (this.breadcrumbs.length < 1) {
        this.$store.commit('setStateProperty', {
          property: 'breadcrumbs',
          value: [{ name: 'Root', id: 'root' }]
        })
      }

      if (fromCrumb) {
        const newBreadcrumbs = []
        this.breadcrumbs.some(crumb => {
          newBreadcrumbs.push(crumb)
          return crumb.id === folder.id
        })
        this.$store.commit('setStateProperty', { property: 'breadcrumbs', value: newBreadcrumbs })
      } else {
        const newBreadcrumbs = [...this.breadcrumbs, folder]
        this.$store.commit('setStateProperty', { property: 'breadcrumbs', value: newBreadcrumbs })
      }

      this.$store.commit('setStateProperty', { property: 'folder', value: folder })
      this.$store.dispatch('fetchDatasets', this.farm.id).then(() => {
        this.$store.dispatch('fetchFolders', this.farm.id)
      })
    },

    isFiguresCompatible (dataset) {
      if (!dataset) return false
      // Rasters are not plottable as tabular data
      if (dataset.file && dataset.file.raster) return false
      if (dataset.file && (dataset.file.geoJsonPath || dataset.file.csvPath)) return true
      if (dataset.dynamic) return true
      if (dataset.data && Array.isArray(dataset.data) && dataset.data.length > 0) return true
      return false
    },

    isAnalysisDataset (dataset) {
      return dataset && dataset.type === 'analysis'
    },

    datasetIcon (dataset) {
      if (!dataset) return 'fas fa-file'
      if (dataset.dynamic) return 'fas fa-satellite-dish'
      if (dataset.type === 'analysis') return 'fas fa-flask'
      if (dataset.type === 'satellite') return 'fas fa-satellite'
      if (dataset.file && dataset.file.raster) return 'fas fa-border-none'
      if (dataset.vizSettings && dataset.vizSettings.type === 'polygon') return 'fas fa-draw-polygon'
      if (dataset.vizSettings && dataset.vizSettings.type === 'line') return 'fas fa-grip-lines-vertical'
      return 'fas fa-map-marker-alt'
    },

    truncate (s) {
      if (!s) return ''
      return s.length > 120 ? s.slice(0, 117) + '…' : s
    }
  },
  filters: {
    formatDate (val) {
      if (!val) return ''
      try {
        const date = val.toDate ? val.toDate() : new Date(val)
        return moment(date).format('MMM D, YYYY')
      } catch (e) {
        return ''
      }
    }
  }
}
</script>

<style scoped>
.dataset-browser { font-size: 0.88rem; }

.db-heading { margin-bottom: 10px; }
.db-heading h6 {
  font-size: 0.82rem;
  font-weight: 600;
  color: #0a4da8;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Breadcrumb chips */
.db-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-wrap: wrap;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  margin-bottom: 8px;
}
.db-crumb {
  background: none;
  border: none;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.78rem;
  color: #495057;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.db-crumb:hover { background: #e9f3fe; color: #0b5ed7; }
.db-crumb.is-current { background: #e9f3fe; color: #0b5ed7; font-weight: 600; cursor: default; }
.db-crumb-sep { color: #adb5bd; font-size: 0.72rem; }

/* Selected dataset banner */
.db-selected {
  padding: 10px 12px;
  background: #f0f7ff;
  border: 1px solid #cfe2ff;
  border-radius: 6px;
  margin-bottom: 10px;
}
.db-selected-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.db-selected-icon {
  color: #0b5ed7;
  font-size: 1.2rem;
  flex-shrink: 0;
}
.db-selected-body { flex: 1; min-width: 0; }
.db-selected-label {
  font-size: 0.68rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.db-selected-body strong {
  font-size: 0.88rem;
  color: #0a4da8;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.db-selected-clear {
  background: none;
  border: 1px solid transparent;
  color: #6c757d;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
}
.db-selected-clear:hover { background: #fff; color: #dc3545; border-color: #f5c2c7; }

/* List */
.db-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #fafbfc;
  padding: 6px;
}
.db-list::-webkit-scrollbar { width: 8px; }
.db-list::-webkit-scrollbar-track { background: transparent; }
.db-list::-webkit-scrollbar-thumb { background: #d0d5dd; border-radius: 4px; }
.db-list::-webkit-scrollbar-thumb:hover { background: #a8b0bb; }

.db-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 10px;
  border: 1px solid transparent;
  border-radius: 5px;
  background: #fff;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: all 0.12s;
}
.db-row:hover {
  border-color: #cfe2ff;
  background: #f8fbff;
  text-decoration: none;
  color: inherit;
  box-shadow: 0 1px 3px rgba(11,94,215,0.08);
}
.db-row.is-active {
  border-color: #0b5ed7;
  background: #e9f3fe;
}
.db-row-icon {
  color: #6c757d;
  font-size: 1.05rem;
  width: 22px;
  text-align: center;
  flex-shrink: 0;
  margin-top: 1px;
}
.db-row-folder .db-row-icon { color: #f4b400; }
.db-row-dataset .db-row-icon { color: #0b5ed7; }
.db-row.is-active .db-row-icon { color: #0a4da8; }

.db-row-body { flex: 1; min-width: 0; line-height: 1.3; }
.db-row-name {
  font-size: 0.86rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.db-row-desc {
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.db-row-meta {
  font-size: 0.7rem;
  color: #868e96;
  margin-top: 2px;
}
.db-row-chevron {
  color: #adb5bd;
  font-size: 0.72rem;
  flex-shrink: 0;
  align-self: center;
}
.db-active-check { color: #0b5ed7; font-size: 0.9rem; }

/* Inline badges */
.db-row-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  padding: 1px 7px;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0.03em;
}
.db-row-badge i { font-size: 0.7em; }
.db-badge-dynamic { background: #e1f5e1; color: #0a6b1c; }
.db-badge-analysis { background: #fff3cd; color: #7a5a00; }

/* Empty state */
.db-empty {
  text-align: center;
  padding: 24px 12px;
  color: #6c757d;
}
.db-empty i {
  font-size: 1.6rem;
  color: #adb5bd;
  margin-bottom: 6px;
  display: block;
}
.db-empty p { font-size: 0.85rem; color: #495057; margin-top: 4px; }
</style>
